import React, { MutableRefObject, useMemo, useRef } from 'react'
import {
  ContentCard,
  Flex,
  Loading,
  TableColumn,
  TableSquare,
  Text,
  TextVariant,
  useScrollVisible
} from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ClaimableByLicenseRow, LicenseStatus, LicenseType } from '@mprolab/sdk'
import { MediaQuery } from '@mprolab/uikit/src/theme/base'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useLifetimeTokens } from 'state/global/hooks'
import { useGetClaimableByLicenseQuery } from 'state/services/indexNode/indexNode'
import { observer } from 'mobx-react-lite'
import { formatPrice } from '../rewards.logic'

/**
 * Per-license unclaimed reward breakdown — implements the "transparent
 * value per license" requirement from the Phase 3 NFT-unfreeze plan
 * (ADR 0002). Each row shows base reward, booster reward and total in
 * MPRO so a license owner (and, post-unfreeze, a marketplace buyer) can
 * see exactly what is attached to each license before deciding to claim
 * or sell.
 */

const Wrapper = styled(ContentCard)`
  flex-direction: column;
`

const ListWrapper = styled(Flex)<{ scrollable: boolean }>`
  overflow-y: auto;
  max-height: 360px;
  flex-direction: column;
  padding-right: ${({ scrollable }) => (scrollable ? '16px' : 'unset')};
  width: 100%;
`

const TotalsRow = styled(Flex)`
  flex-wrap: wrap;
  gap: 24px;
  padding: 12px 4px 16px;
  align-items: baseline;
`

const TotalChip = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`

function licenseTypeLabel(t: (k: string) => string, type: string | LicenseType): string {
  switch (type) {
    case LicenseType.MGA_INODE:
      return t('general.iNode')
    case LicenseType.MGA_LIGHT:
      return t('general.ggNode')
    case LicenseType.MGA_REGULAR:
      return t('general.mga')
    default:
      return String(type)
  }
}

const ClaimableByLicense: React.FC = observer(() => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const wrapper = useRef<HTMLDivElement>(null)
  const scrollVisible = useScrollVisible(wrapper as MutableRefObject<HTMLDivElement>)
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]

  const { data, isLoading } = useGetClaimableByLicenseQuery(
    { account, accessToken },
    {
      skip: !account || !accessToken,
      refetchOnMountOrArgChange: true
    }
  )

  const columns = useMemo(
    (): TableColumn[] => [
      {
        key: 'license',
        name: t('rewards.license'),
        gridColumns: 3,
        customComponent(row: ClaimableByLicenseRow) {
          const label = row.name?.trim() || row.licenseId.slice(0, 8)
          return (
            <Flex flexDirection="column">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1}>
                {label}
              </Text>
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {licenseTypeLabel(t, row.type)}
              </Text>
            </Flex>
          )
        }
      },
      {
        key: 'status',
        name: t('rewards.licenseStatus'),
        gridColumns: 2,
        customComponent(row: ClaimableByLicenseRow) {
          const isTerminated = row.status === LicenseStatus.TERMINATED
          return (
            <Text
              variant={TextVariant.BODY_SMALL}
              color={isTerminated ? colors.red : colors.white}
            >
              {row.status}
            </Text>
          )
        }
      },
      {
        key: 'baseReward',
        name: `${t('rewards.baseReward')} [MPRO]`,
        gridColumns: 2.4,
        align: 'flex-end',
        customComponent(row: ClaimableByLicenseRow) {
          return (
            <Flex justifyContent="flex-end">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1}>
                {formatPrice(row.unclaimedBaseReward)}
              </Text>
            </Flex>
          )
        }
      },
      {
        key: 'boosterReward',
        name: `${t('rewards.boosterReward')} [MPRO]`,
        gridColumns: 2.4,
        align: 'flex-end',
        customComponent(row: ClaimableByLicenseRow) {
          return (
            <Flex justifyContent="flex-end">
              <Text variant={TextVariant.BODY_SMALL_BOLD} isTrimmed linesToDisplay={1}>
                {formatPrice(row.unclaimedBoosterReward)}
              </Text>
            </Flex>
          )
        }
      },
      {
        key: 'totalReward',
        name: `${t('rewards.unclaimedTotal')} [MPRO]`,
        gridColumns: 2.2,
        align: 'flex-end',
        customComponent(row: ClaimableByLicenseRow) {
          return (
            <Flex justifyContent="flex-end">
              <Text
                variant={TextVariant.BODY_SMALL_BOLD}
                isTrimmed
                linesToDisplay={1}
                color={colors.cyan}
              >
                {formatPrice(row.unclaimedTotalReward)}
              </Text>
            </Flex>
          )
        }
      }
    ],
    [colors.cyan, colors.red, colors.gray6, colors.white, t]
  )

  const totals = data ?? {
    totalUnclaimedBase: 0,
    totalUnclaimedBooster: 0,
    totalUnclaimed: 0,
    licenses: []
  }

  return (
    <Wrapper>
      <Flex flexDirection="column" gap="6px">
        <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
          {t('rewards.claimableByLicenseTitle')}
        </Text>
      </Flex>
      <TotalsRow>
        <TotalChip>
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
            {t('rewards.unclaimedBaseTotal')}
          </Text>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
            {formatPrice(totals.totalUnclaimedBase)} MPRO
          </Text>
        </TotalChip>
        <TotalChip>
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
            {t('rewards.unclaimedBoosterTotal')}
          </Text>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
            {formatPrice(totals.totalUnclaimedBooster)} MPRO
          </Text>
        </TotalChip>
        <TotalChip>
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
            {t('rewards.unclaimedGrandTotal')}
          </Text>
          <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.cyan}>
            {formatPrice(totals.totalUnclaimed)} MPRO
          </Text>
        </TotalChip>
      </TotalsRow>
      <ListWrapper ref={wrapper} scrollable={scrollVisible}>
        {isLoading ? (
          <Flex fullWidth justifyContent="center" py="32px">
            <Loading />
          </Flex>
        ) : (
          <TableSquare
            tableTitle={t('rewards.claimableByLicenseTableTitle')}
            plain
            data={totals.licenses}
            emptyListComponent={
              <Text textAlign="center">{t('rewards.noClaimableByLicense')}</Text>
            }
            fullViewMediaquery={MediaQuery.tablet}
            fullViewCustomWidth={900}
            {...{ columns }}
          />
        )}
      </ListWrapper>
    </Wrapper>
  )
})

export default ClaimableByLicense
